/**
 * Centralized HTTP / network error classification.
 *
 * Callers receive a stable {@link ERROR_TYPES} value rather than raw status
 * codes or error messages. This:
 *   • prevents leaking backend internals (we never read response body for errors)
 *   • makes UI labels consistent (map ERROR_TYPE → label via messages.js)
 *   • localizes status-code knowledge to one file
 *
 * To add a new error category: add a constant in ERROR_TYPES, map it from
 * status code in STATUS_TO_TYPE (if HTTP-based) or add a discriminator in
 * classifyNetworkError, then add a label in messages.js ERROR_LABELS.
 */

export const ERROR_TYPES = Object.freeze({
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  FORBIDDEN: 'FORBIDDEN',
  SERVER_ERROR: 'SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  NETWORK_OFFLINE: 'NETWORK_OFFLINE',
  UNKNOWN: 'UNKNOWN',
});

// Single source of truth for HTTP status → ERROR_TYPE mapping.
const STATUS_TO_TYPE = Object.freeze({
  401: ERROR_TYPES.AUTH_EXPIRED,
  403: ERROR_TYPES.FORBIDDEN,
  503: ERROR_TYPES.SERVICE_UNAVAILABLE,
});

/**
 * Map an HTTP {@link Response} to an ERROR_TYPE based on status code.
 * Pure function: does not read response body.
 */
export function classifyHttpError(response) {
  if (!response) return ERROR_TYPES.UNKNOWN;
  const mapped = STATUS_TO_TYPE[response.status];
  if (mapped) return mapped;
  if (response.status >= 500) return ERROR_TYPES.SERVER_ERROR;
  return ERROR_TYPES.UNKNOWN;
}

/**
 * Classify a fetch() rejection (network error). We intentionally don't read
 * the error's message — its content varies across browsers and may include
 * internal details we don't want to surface.
 */
// eslint-disable-next-line no-unused-vars
export function classifyNetworkError(err) {
  return ERROR_TYPES.NETWORK_OFFLINE;
}
